import { ArchiveBoxIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import type { PatientNote, NoteSeverity } from '../../../services/patientNotes';
import type { TiptapDoc } from '@odo/shared-ui';
import {
    usePatientNotes,
    useCreateNote,
    useUpdateNote,
    useDeleteNote,
    useArchiveNote
} from '../../../hooks/usePatientNotes';
import { NoteModal } from './NoteModal';
import { NotesList } from './NotesList';
import { DeleteNoteConfirmModal } from './DeleteNoteConfirmModal';

/**
 * Full-CRUD notes tab with an archive/unarchive toggle, shared by the
 * patient-files Notes tab (compact=false) and work-queue sidebar (compact=true).
 */
interface NotesTabProps {
    patientId: string;
    /** Tighter paddings + smaller "Add note" button for narrow columns (e.g. the work queue). */
    compact?: boolean;
}

function NotesTab({ patientId, compact = false }: NotesTabProps) {
    const { notes, archivedNotes, isLoading } = usePatientNotes(patientId);
    const createNote = useCreateNote(patientId);
    const updateNote = useUpdateNote(patientId);
    const deleteNote = useDeleteNote(patientId);
    const archiveNote = useArchiveNote(patientId);

    const [editingNote, setEditingNote] = useState<PatientNote | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [deletingNote, setDeletingNote] = useState<PatientNote | null>(null);
    const [showArchived, setShowArchived] = useState(false);

    const handleCreate = (data: { severity: NoteSeverity; title: string; bodyDoc: TiptapDoc }) => {
        createNote.mutate(data, { onSuccess: () => setIsCreating(false) });
    };

    const handleUpdate = (data: { severity: NoteSeverity; title: string; bodyDoc: TiptapDoc }) => {
        if (!editingNote) return;
        updateNote.mutate(
            { noteId: editingNote.id, body: data },
            { onSuccess: () => setEditingNote(null) }
        );
    };

    const handlePin = (noteId: string, pinned: boolean) => {
        updateNote.mutate({ noteId, body: { pinned } });
    };

    const handleArchive = (noteId: string) => {
        archiveNote.mutate({ noteId, archived: true });
    };

    const handleUnarchive = (noteId: string) => {
        archiveNote.mutate({ noteId, archived: false });
    };

    const handleDeleteRequest = (noteId: string) => {
        const source = showArchived ? archivedNotes : notes;
        setDeletingNote(source.find((n) => n.id === noteId) ?? null);
    };

    const handleDeleteConfirm = () => {
        if (!deletingNote) return;
        deleteNote.mutate(deletingNote.id, { onSuccess: () => setDeletingNote(null) });
    };

    const archiveToggleBtn = compact ? 'p-1 rounded-lg' : 'px-2 py-2 rounded-xl';
    const archiveToggleIcon = compact ? 'w-3.5 h-3.5' : 'w-4 h-4';

    const archiveToggle = (
        <button
            type="button"
            onClick={() => setShowArchived((s) => !s)}
            title="Archived notes"
            className={`inline-flex items-center justify-center gap-1 border transition-colors flex-shrink-0 ${archiveToggleBtn} ${
                showArchived
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-100'
            }`}
        >
            <ArchiveBoxIcon className={archiveToggleIcon} />
            {archivedNotes.length > 0 && (
                <span
                    className={`text-[10px] font-bold leading-none ${showArchived ? 'text-white' : 'text-gray-400'}`}
                >
                    {archivedNotes.length}
                </span>
            )}
        </button>
    );

    const addNoteButton = (
        <button
            type="button"
            onClick={() => setIsCreating(true)}
            title="Add note"
            aria-label="Add note"
            className={`inline-flex items-center justify-center bg-gray-900 text-white hover:bg-black transition-colors shadow-sm flex-shrink-0 ${
                compact ? 'p-1 rounded-lg' : 'p-2 rounded-xl'
            }`}
        >
            <PlusIcon className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
        </button>
    );

    return (
        <div className="flex flex-col h-full">
            {showArchived ? (
                <NotesList
                    notes={archivedNotes}
                    isLoading={isLoading}
                    compact={compact}
                    archived
                    archivedActions={{
                        onUnarchive: handleUnarchive,
                        onDelete: handleDeleteRequest
                    }}
                    headerRight={archiveToggle}
                />
            ) : (
                <NotesList
                    notes={notes}
                    isLoading={isLoading}
                    compact={compact}
                    actions={{
                        onEdit: setEditingNote,
                        onDelete: handleDeleteRequest,
                        onPin: handlePin,
                        onArchive: handleArchive
                    }}
                    headerRight={
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                            {archiveToggle}
                            {addNoteButton}
                        </div>
                    }
                    emptyAction={
                        <button
                            onClick={() => setIsCreating(true)}
                            className="text-xs font-semibold font-inter text-gray-600 underline underline-offset-2"
                        >
                            Add the first note
                        </button>
                    }
                />
            )}

            {isCreating && (
                <NoteModal
                    patientId={patientId}
                    note={null}
                    isSaving={createNote.isPending}
                    onSave={handleCreate}
                    onClose={() => setIsCreating(false)}
                />
            )}

            {editingNote && (
                <NoteModal
                    patientId={patientId}
                    note={editingNote}
                    isSaving={updateNote.isPending}
                    onSave={handleUpdate}
                    onClose={() => setEditingNote(null)}
                />
            )}

            {deletingNote && (
                <DeleteNoteConfirmModal
                    note={deletingNote}
                    isDeleting={deleteNote.isPending}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setDeletingNote(null)}
                />
            )}
        </div>
    );
}

export { NotesTab };
